import { Button, Modal, SelectMenu, TextField, Typography, Autocomplete } from '@scale/scaleui';
import { SquadMemberRole, SquadType } from '@scale/shared/types/expertManagement';
import { useCallback, useEffect, useState } from 'react';
import { useCreateCommunityChannel } from 'react-scale/components/ExpertManagement/hooks/mutations/useCreateCommunityChannel';
import { useUpsertSquad } from 'react-scale/components/ExpertManagement/hooks/mutations/useUpsertSquad';
import { useFetchCommunityChannels } from 'react-scale/components/ExpertManagement/hooks/queries/useFetchCommunityChannels';
import {
  podTypeOptionsSelector,
  useStatContextStore,
} from 'react-scale/components/ExpertManagement/stores/statContext.store';
import { defaultApiFetchErrorHandler } from 'react-scale/lib/api-fetch';
import shallow from 'zustand/shallow';
import { Tooltip } from 'design-system';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

const SQUAD_TYPE_OPTIONS = [
  { id: SquadType.TeamLeadCoordinator, name: 'Team Lead Coordinator', disabled: true },
  { id: SquadType.Squad, name: 'Squad' },
  { id: SquadType.Community, name: 'Community' },
  // { id: SquadType.CommunitySubSquad, name: 'Community SubSquad' },
];

interface EditSquadInfoModalProps {
  onClose: () => void;
  open: boolean;
  refetch: () => Promise<void>;
  squad: any;
}

interface SquadLeaderInfo {
  id: string;
  name: string;
  email: string;
  role: SquadMemberRole;
}

export function EditSquadInfoModal({ onClose, open, refetch, squad }: EditSquadInfoModalProps) {
  const [name, setName] = useState(squad?.name);
  const [type, setType] = useState(squad?.type);
  const [podType, setPodType] = useState(squad?.teamLeadPodType);
  const [communityChannelSearchString, setCommunityChannelSearchString] = useState<string>(
    squad?.communityChannel ?? '',
  );
  const podTypeOptions = useStatContextStore(podTypeOptionsSelector, shallow);

  const {
    data: channelsData,
    refetch: refetchCommunityChannels,
    isLoading: isLoadingCommunityChannels,
    isFetching: isFetchingCommunityChannels,
  } = useFetchCommunityChannels(communityChannelSearchString);

  const communityChannelOptions = channelsData?.communityChannels.map(
    channel => `${channel._id} - ${channel.name}`,
  );

  const communityChannel = channelsData?.communityChannels.find(
    channel => channel._id === communityChannelSearchString,
  );

  const admin = squad?.leaders.find(
    (m: SquadLeaderInfo) =>
      (squad?.type === SquadType.Squad && m.role === SquadMemberRole.TeamLead) ||
      (squad?.type === SquadType.TeamLeadCoordinator &&
        m.role === SquadMemberRole.TeamLeadCoordinator),
  );

  useEffect(() => {
    setName(squad?.name);
    setType(squad?.type);
    setPodType(squad?.teamLeadPodType);
    setCommunityChannelSearchString(squad?.communityChannel ?? '');
  }, [squad]);

  const { mutateAsync: upsertSquad, isLoading: upsertLoading } = useUpsertSquad();

  const { mutateAsync: createCommunityChannel, isLoading: isCreatingChannel } =
    useCreateCommunityChannel();

  const handleSave = useCallback(async () => {
    try {
      await upsertSquad({
        squadId: squad?._id,
        name,
        type,
        teamLeadPodTypeId: podType,
        communityChannelId: communityChannel?._id,
      });
      await refetch();
      onClose();
    } catch (e) {
      defaultApiFetchErrorHandler(e, { handleHttp: true });
    }
  }, [name, onClose, podType, refetch, squad?._id, type, upsertSquad, communityChannel]);

  const podTypeName = podTypeOptions.find(p => p.id === squad?.teamLeadPodType)?.name;

  const handleCreateChannel = useCallback(async () => {
    if (!squad || !admin?.id) return;
    try {
      const response = await createCommunityChannel({
        squad,
        adminId: admin.id,
        communityChannelName:
          squad.type === SquadType.TeamLeadCoordinator
            ? `Squad Reviewers ${podTypeName}`
            : undefined,
      });
      if (response.success && response.communityChannel) {
        squad.communityChannel = response.communityChannel;
        setCommunityChannelSearchString(response.communityChannel);
        await refetch();
      }
    } catch (e) {
      defaultApiFetchErrorHandler(e, { handleHttp: true });
    }
  }, [squad, admin, createCommunityChannel, podTypeName, refetch]);

  const loadingChannels =
    isLoadingCommunityChannels || isFetchingCommunityChannels || isCreatingChannel;

  const invalidChannel =
    !loadingChannels && communityChannelSearchString.length > 0 && !communityChannel;

  return (
    <Modal title="Edit Manager Info" open={open} onClose={onClose}>
      <div
        className="flex flex-col justify-between"
        style={{ minHeight: '50vh', minWidth: '50vw' }}
      >
        <div className="flex flex-col gap-4">
          <div className="flex flex-col gap-1">
            <Typography.Subtitle3 className="col-span-1">Squad Name</Typography.Subtitle3>
            <TextField value={name} onChange={t => setName(t.target.value)} />
          </div>
          <div className="flex flex-col gap-1">
            <Typography.Subtitle3 className="col-span-1">Squad Type</Typography.Subtitle3>
            <SelectMenu
              selectedOptionId={type}
              setSelected={r => setType(r)}
              disabled={![SquadType.Community, SquadType.Squad].includes(squad?.type)}
              options={SQUAD_TYPE_OPTIONS}
            />
          </div>
          <div className="flex flex-col gap-1">
            <Typography.Subtitle3 className="col-span-1">Pod Type</Typography.Subtitle3>
            <SelectMenu
              selectedOptionId={podType}
              setSelected={r => setPodType(r)}
              options={podTypeOptions}
              disabled
            />
          </div>
          <div className="flex flex-col gap-1">
            <Autocomplete
              options={communityChannelOptions ?? []}
              label="Community Channel ID (Optional)"
              value={communityChannelSearchString}
              onInputChange={(input: string) => {
                setCommunityChannelSearchString(input);
                refetchCommunityChannels();
              }}
              setSelectedOption={(option: string) => {
                setCommunityChannelSearchString(option.split(' ')[0] || '');
              }}
              canCreateOption={false}
            />
            {invalidChannel && (
              <Typography.Paragraph2 className="text-danger-500">
                Invalid community channel ID
              </Typography.Paragraph2>
            )}
          </div>
          {!squad?.communityChannel && (
            <div className="flex flex-row gap-1 items-center">
              <Button
                disabled={!admin || loadingChannels || communityChannelSearchString.length > 0}
                isLoading={isCreatingChannel}
                onClick={handleCreateChannel}
              >
                Create and Populate With Default Channel
              </Button>
              <Tooltip
                title={`This will create a Discourse community channel and category named ${
                  squad?.type === SquadType.TeamLeadCoordinator
                    ? `"Squad Reviewers {Pod Type}"`
                    : `"Squad {Squad Reviewer Name}"`
                }`}
                placement="bottom"
                arrow
                interactive
              >
                <FontAwesomeIcon icon="info-circle" />
              </Tooltip>
            </div>
          )}
        </div>
        <div className="flex justify-end gap-1">
          <Button onClick={onClose} variant="danger">
            Cancel
          </Button>
          <Button disabled={invalidChannel || upsertLoading} onClick={handleSave}>
            {upsertLoading ? 'Updating...' : 'Update Squad'}
          </Button>
        </div>
      </div>
    </Modal>
  );
}
